/**
 * useDocumentKeyEntities
 *
 * Hook returning the top GraphRAG entities (people / orgs / places /
 * events) for a single document. Powers the per-row inventory badge.
 *
 * Lazy + cached:
 *   * Inventory is virtualized — rows mount/unmount as the user
 *     scrolls. A naive per-row fetch would re-hit the API every
 *     scroll. A module-level cache means each document is fetched
 *     at most once per page load, even across re-mounts.
 *   * In-flight requests are coalesced via a shared promise so
 *     simultaneously-mounted rows for the same document share one
 *     HTTP call. (Shouldn't usually happen but cheap to guard.)
 *   * 404 / non-graph documents land in the cache as ``[]`` so we
 *     don't keep retrying.
 *
 * The endpoint is feature-flagged on the backend
 * (``GRAPHRAG_INVENTORY_BADGES``); when off it returns ``[]`` and
 * the badge component renders nothing.
 */

import { useEffect, useState } from 'react';
import { api } from '../api';

// docId → { entities: [...], total: number }
const _cache = new Map();
// docId → Promise<{entities, total}>
const _inflight = new Map();

async function _fetch(docId) {
  if (_cache.has(docId)) return _cache.get(docId);
  if (_inflight.has(docId)) return _inflight.get(docId);

  const p = (async () => {
    try {
      const data = await api.get(`/documents/${docId}/key-entities?limit=6`);
      const result = {
        entities: Array.isArray(data?.entities) ? data.entities : [],
        total: typeof data?.total === 'number' ? data.total : 0,
      };
      _cache.set(docId, result);
      return result;
    } catch {
      // Cache the empty result so a transient 5xx (or a doc with
      // no graph_entities yet) doesn't trigger repeated fetches on
      // every scroll. The user can refresh the page to retry.
      const empty = { entities: [], total: 0 };
      _cache.set(docId, empty);
      return empty;
    } finally {
      _inflight.delete(docId);
    }
  })();
  _inflight.set(docId, p);
  return p;
}

export default function useDocumentKeyEntities(docId, { enabled = true } = {}) {
  const [state, setState] = useState(() =>
    docId && _cache.has(docId)
      ? { entities: _cache.get(docId).entities, total: _cache.get(docId).total, loading: false }
      : { entities: [], total: 0, loading: true }
  );

  useEffect(() => {
    if (!docId || !enabled) {
      setState({ entities: [], total: 0, loading: false });
      return;
    }
    if (_cache.has(docId)) {
      const cached = _cache.get(docId);
      setState({ entities: cached.entities, total: cached.total, loading: false });
      return;
    }
    let cancelled = false;
    setState(s => ({ ...s, loading: true }));
    _fetch(docId).then(r => {
      if (cancelled) return;
      setState({ entities: r.entities, total: r.total, loading: false });
    });
    return () => { cancelled = true; };
  }, [docId, enabled]);

  return state;
}

// Test-only escape hatch — lets unit tests reset the cache between runs.
export function _resetKeyEntitiesCache() {
  _cache.clear();
  _inflight.clear();
}
